import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import "./DateTimePicker.css";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DAYS = ["M", "T", "W", "T", "F", "S", "S"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  const day = new Date(year, month, 1).getDay(); // 0 = Sun
  return (day + 6) % 7; // Monday = 0
}

function pad(n) {
  return String(n).padStart(2, "0");
}

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

// ─── TimeColumn ───────────────────────────────────────────────────────────────
/**
 * Renders the selected value in a blue chip at top, then a scrollable list
 * of all items below — exactly like the Teams-style reference screenshots.
 */
const TimeColumn = ({ items, value, onChange, isDisabled = () => false }) => {
  const listRef = useRef(null);

  // Auto-scroll the list so the selected item is near the top
  useEffect(() => {
    const idx = items.indexOf(value);
    if (listRef.current && idx !== -1) {
      // Scroll so selected item is first visible
      listRef.current.scrollTop = idx * 36;
    }
  }, [value, items]);

  return (
    <div className="dtp-time-col">
      {/* Selected value chip */}
      <div className="dtp-time-chip">{pad(value)}</div>

      {/* Scrollable list of all items */}
      <ul ref={listRef} className="dtp-time-list">
        {items.map((item) => (
          <li
            key={item}
            className={`dtp-time-list-item ${item === value ? "dtp-time-list-item--active" : ""} ${isDisabled(item) ? "dtp-time-list-item--disabled" : ""}`}
            onClick={() => !isDisabled(item) && onChange(item)}
          >
            {pad(item)}
          </li>
        ))}
      </ul>
    </div>
  );
};

// ─── Calendar Grid ────────────────────────────────────────────────────────────

const CalendarGrid = ({ year, month, selectedDate, onSelectDate, minDate }) => {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const prevMonthDays = getDaysInMonth(year, month === 0 ? 11 : month - 1);
  const prevYear = month === 0 ? year - 1 : year;
  const prevMonth = month === 0 ? 11 : month - 1;

  const cells = [];

  for (let i = 0; i < firstDay; i++) {
    const day = prevMonthDays - firstDay + 1 + i;
    cells.push({ day, month: prevMonth, year: prevYear, outside: true });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, month, year, outside: false });
  }
  const trailing = 42 - cells.length;
  const nextMonth = month === 11 ? 0 : month + 1;
  const nextYear = month === 11 ? year + 1 : year;
  for (let d = 1; d <= trailing; d++) {
    cells.push({ day: d, month: nextMonth, year: nextYear, outside: true });
  }

  const today = new Date();

  return (
    <div className="dtp-grid">
      {DAYS.map((d, i) => (
        <div key={i} className="dtp-grid-header">{d}</div>
      ))}
      {cells.map((cell, i) => {
        const cellDate = new Date(cell.year, cell.month, cell.day);
        const isSelected = selectedDate && isSameDay(cellDate, selectedDate);
        const isToday = isSameDay(cellDate, today);
        const isDisabled = minDate && (
          cellDate.getFullYear() < minDate.getFullYear() ||
          (cellDate.getFullYear() === minDate.getFullYear() && cellDate.getMonth() < minDate.getMonth()) ||
          (cellDate.getFullYear() === minDate.getFullYear() && cellDate.getMonth() === minDate.getMonth() && cellDate.getDate() < minDate.getDate())
        );

        return (
          <button
            key={i}
            className={[
              "dtp-day",
              cell.outside ? "dtp-day--outside" : "",
              isSelected ? "dtp-day--selected" : "",
              isToday && !isSelected ? "dtp-day--today" : "",
              isDisabled ? "dtp-day--disabled" : "",
            ].join(" ")}
            onClick={() => !isDisabled && onSelectDate(cellDate)}
            disabled={isDisabled}
            aria-label={`${cell.day} ${MONTHS[cell.month]} ${cell.year}`}
            aria-pressed={isSelected}
          >
            {cell.day}
          </button>
        );
      })}
    </div>
  );
};

// ─── Popup (rendered via Portal at document.body) ─────────────────────────────

const DateTimePopup = ({
  triggerRef,
  viewYear, viewMonth,
  pickerDate, pickerHour, pickerMinute,
  onPrevMonth, onNextMonth,
  onSelectDate, onHourChange, onMinuteChange,
  onClear, onToday,
  minDate,
  onClose,
}) => {
  const popupRef = useRef(null);
  const [style, setStyle] = useState({ visibility: "hidden" });

  // Position the popup relative to the trigger using fixed coords
  useEffect(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const popupWidth = 410; // approximate width
    const popupHeight = 340; // approximate height
    const gap = 6;

    let top = rect.bottom + gap;
    let left = rect.left;

    // Flip upward if not enough space below
    if (top + popupHeight > window.innerHeight - 10) {
      top = rect.top - popupHeight - gap;
    }

    // Clamp to viewport horizontally
    if (left + popupWidth > window.innerWidth - 10) {
      left = window.innerWidth - popupWidth - 10;
    }
    if (left < 8) left = 8;

    setStyle({ top, left, visibility: "visible" });
  }, [triggerRef]);

  // Close on outside click
  useEffect(() => {
    const handle = (e) => {
      if (
        popupRef.current && !popupRef.current.contains(e.target) &&
        triggerRef.current && !triggerRef.current.contains(e.target)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [onClose, triggerRef]);

  // Close on Escape
  useEffect(() => {
    const handle = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handle);
    return () => document.removeEventListener("keydown", handle);
  }, [onClose]);

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  return ReactDOM.createPortal(
    <div
      ref={popupRef}
      className="dtp-popup"
      style={style}
      role="dialog"
      aria-modal="true"
      aria-label="Date and time picker"
    >
      {/* ── Calendar Section ── */}
      <div className="dtp-calendar-section">
        {/* Month navigation */}
        <div className="dtp-month-nav">
          <span className="dtp-month-label">
            {MONTHS[viewMonth]} {viewYear}
          </span>
          <div className="dtp-nav-btns">
            <button className="dtp-nav-btn" onClick={onPrevMonth} aria-label="Previous month">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button className="dtp-nav-btn" onClick={onNextMonth} aria-label="Next month">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>

        {/* Calendar grid */}
        <CalendarGrid
          year={viewYear}
          month={viewMonth}
          selectedDate={pickerDate}
          onSelectDate={onSelectDate}
          minDate={minDate}
        />

        {/* Footer actions */}
        <div className="dtp-footer">
          <button className="dtp-footer-btn" onClick={onClear}>Clear</button>
          <div className="dtp-footer-right">
            <button className="dtp-footer-btn dtp-footer-btn--today" onClick={onToday}>Today</button>
            <button className="dtp-footer-btn dtp-footer-btn--save" onClick={onClose}>Save</button>
          </div>
        </div>
      </div>

      {/* ── Divider ── */}
      <div className="dtp-divider" />

      {/* ── Time Section ── */}
      <div className="dtp-time-section">
        <TimeColumn
          items={hours}
          value={pickerHour}
          onChange={onHourChange}
          isDisabled={(hour) => !!minDate && isSameDay(pickerDate, minDate) && hour < minDate.getHours()}
        />
        <TimeColumn
          items={minutes}
          value={pickerMinute}
          onChange={onMinuteChange}
          isDisabled={(minute) => !!minDate && isSameDay(pickerDate, minDate) && pickerHour === minDate.getHours() && minute < minDate.getMinutes()}
        />
      </div>
    </div>,
    document.body
  );
};

// ─── Main DateTimePicker ──────────────────────────────────────────────────────

/**
 * DateTimePicker
 *
 * Props:
 *   value         {Date|null}   – Currently selected date+time
 *   onChange      {Function}    – Called with new Date when selection changes
 *   placeholder   {string}      – Input placeholder text
 *   minDate       {Date|null}   – Earliest selectable date
 *   label         {string}      – Optional label above input
 *   id            {string}      – ID for the input (a11y)
 *   disabled      {boolean}     – Disable the picker
 */
const DateTimePicker = ({
  value = null,
  onChange,
  placeholder = "Select date & time",
  minDate = null,
  label = "",
  id = "date-time-picker",
  disabled = false,
}) => {
  const today = React.useMemo(() => new Date(), []);

  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState((value || today).getFullYear());
  const [viewMonth, setViewMonth] = useState((value || today).getMonth());

  const [pickerDate, setPickerDate] = useState(value || today);
  const [pickerHour, setPickerHour] = useState(value ? value.getHours() : 9);
  const [pickerMinute, setPickerMinute] = useState(value ? value.getMinutes() : 0);

  const triggerRef = useRef(null);

  // Sync when external value changes
  useEffect(() => {
    if (value) {
      setPickerDate(value);
      setPickerHour(value.getHours());
      setPickerMinute(value.getMinutes());
      setViewYear(value.getFullYear());
      setViewMonth(value.getMonth());
    }
  }, [value]);

  const clampToMinDateTime = useCallback((date) => (
    minDate && date < minDate ? new Date(minDate) : date
  ), [minDate]);

  const handleSelectDate = useCallback((date) => {
    const combined = new Date(date);
    combined.setHours(pickerHour, pickerMinute, 0, 0);
    const nextDate = clampToMinDateTime(combined);
    setPickerDate(nextDate);
    setPickerHour(nextDate.getHours());
    setPickerMinute(nextDate.getMinutes());
    setViewYear(nextDate.getFullYear());
    setViewMonth(nextDate.getMonth());
    onChange && onChange(nextDate);
  }, [pickerHour, pickerMinute, onChange, clampToMinDateTime]);

  const handleHourChange = useCallback((h) => {
    const combined = new Date(pickerDate);
    combined.setHours(h, pickerMinute, 0, 0);
    const nextDate = clampToMinDateTime(combined);
    setPickerDate(nextDate);
    setPickerHour(nextDate.getHours());
    setPickerMinute(nextDate.getMinutes());
    onChange && onChange(nextDate);
  }, [pickerDate, pickerMinute, onChange, clampToMinDateTime]);

  const handleMinuteChange = useCallback((m) => {
    const combined = new Date(pickerDate);
    combined.setHours(pickerHour, m, 0, 0);
    const nextDate = clampToMinDateTime(combined);
    setPickerDate(nextDate);
    setPickerHour(nextDate.getHours());
    setPickerMinute(nextDate.getMinutes());
    onChange && onChange(nextDate);
  }, [pickerDate, pickerHour, onChange, clampToMinDateTime]);

  const prevMonth = useCallback(() => {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  }, [viewMonth]);

  const nextMonth = useCallback(() => {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  }, [viewMonth]);

  const handleClear = useCallback(() => {
    setPickerDate(today);
    setPickerHour(9);
    setPickerMinute(0);
    onChange && onChange(null);
  }, [onChange, today]);

  const handleToday = useCallback(() => {
    const now = new Date();
    setPickerDate(now);
    setPickerHour(now.getHours());
    setPickerMinute(now.getMinutes());
    setViewYear(now.getFullYear());
    setViewMonth(now.getMonth());
    const combined = clampToMinDateTime(new Date(now));
    combined.setSeconds(0, 0);
    setPickerDate(combined);
    setPickerHour(combined.getHours());
    setPickerMinute(combined.getMinutes());
    setViewYear(combined.getFullYear());
    setViewMonth(combined.getMonth());
    onChange && onChange(combined);
  }, [onChange, clampToMinDateTime]);

  const displayValue = value
    ? `${pad(value.getDate())}/${pad(value.getMonth() + 1)}/${value.getFullYear()}, ${pad(value.getHours())}:${pad(value.getMinutes())}`
    : "";

  return (
    <div className="dtp-root">
      {label && (
        <label className="dtp-label" htmlFor={id}>{label}</label>
      )}

      {/* ── Trigger Input ── */}
      <div
        ref={triggerRef}
        className={[
          "dtp-input-wrapper",
          open ? "dtp-input-wrapper--open" : "",
          disabled ? "dtp-input-wrapper--disabled" : "",
        ].join(" ")}
        onClick={() => !disabled && setOpen(o => !o)}
        id={id}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => e.key === "Enter" && !disabled && setOpen(o => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className={`dtp-input-text ${!displayValue ? "dtp-input-placeholder" : ""}`}>
          {displayValue || placeholder}
        </span>
        <span className="dtp-calendar-icon" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        </span>
      </div>

      {/* ── Popup rendered via Portal so it escapes any overflow:hidden parent ── */}
      {open && (
        <DateTimePopup
          triggerRef={triggerRef}
          viewYear={viewYear}
          viewMonth={viewMonth}
          pickerDate={pickerDate}
          pickerHour={pickerHour}
          pickerMinute={pickerMinute}
          onPrevMonth={prevMonth}
          onNextMonth={nextMonth}
          onSelectDate={handleSelectDate}
          onHourChange={handleHourChange}
          onMinuteChange={handleMinuteChange}
          onClear={handleClear}
          onToday={handleToday}
          minDate={minDate}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
};

export default DateTimePicker;
